import React, { useContext, useCallback, useMemo } from 'react';
import cIf from 'classnames';

import './selected-ticket-footer.scss';
import TranslatorManager from 'ts-translate-js';
import { getSelectionItemsTotalAndFees } from '../../../mvcapi/ticket-price';
import { PlaceToGoEnum } from '../../../../jsxctl/utilctl';
import { Warning } from '../../../logic/models/ticketselectionwarning';
import { TicketSelectionWarningList } from './ticket-selection-warning-list';
import { useIFrameResize } from '../../../logic/hooks/utils';
import { SiteCustomizationContext } from '../../../logic/reactcontext/site-customization-context';
import { AddToCartStoreResult, useAddTicketsToCartStore, useBaseSiteStore } from '../../../stores';
import { useMVCPriceGroupModelsCache } from '../../../logic/hooks/caching/use-mvc-price-group-model-cache';
import { trackEcommerceEvent } from '../../../../js/client-tracking';
import { AddToCartItem } from '../../../stores/add-to-cart-stores/models/add-to-cart-items';
import { prepareNewAddTicketsToCartTrackingData } from '../../../logic/tracking-logic';
import { useAddPackageToCartStoreExt } from '../../../stores/add-to-cart-stores/add-package-to-cart-store-ext';
import toastr from 'toastr';
import { is } from 'immer/dist/internal';

const accounting = require('accounting');

const tr = TranslatorManager.boundTR; 

interface SelectedTicketFooterProps {
    //selectedTickets: SelectedTicketInfo[];
    items: AddToCartItem[];
    warnings: Warning[];
    disabled: boolean;
}

export function SelectedTicketFooter({items, warnings, disabled}: SelectedTicketFooterProps) {
    useIFrameResize();
    const { siteSettings } = useContext(SiteCustomizationContext);
    const integ = useBaseSiteStore(s => s.integ);
    const [ , , priceGroupByIdCache ] = useMVCPriceGroupModelsCache();
    const addToCart = useAddTicketsToCartStore(s => s.addToCart);
    const eventDate = useAddTicketsToCartStore(s => s.eventDate);
    const afterAddedToCartCallback = useAddTicketsToCartStore(s => s.afterAddedToCartCallback);

    const addPackageToCart = useAddPackageToCartStoreExt(s => s.addPackageToCart);
    const isAPackageSale = useAddPackageToCartStoreExt(s => s.isAPackageSale);

    // const tickeCummul = useMemo(() => {
    //     return getTicketPriceTotalAndFees(selectedTickets);
    // }, [selectedTickets]);

    const tickeCummul = useMemo(() => getSelectionItemsTotalAndFees(items, siteSettings), [items, siteSettings]);

    const trackAddedToCart = useCallback(async (result: AddToCartStoreResult) => {
        try {
            //TODO: Add package tracking support
            const trackingData = prepareNewAddTicketsToCartTrackingData(
                tsapp.currentCurrency, result.addToCartRequest, eventDate.ProductNameFr, eventDate.CategoryFr, eventDate.EventDateCode, priceGroupByIdCache
            );

            await trackEcommerceEvent(integ, trackingData);
        } catch (err) { console.log(err); }
    }, [eventDate, integ, priceGroupByIdCache]);
    
    const handleAddToCart = useCallback(async (placeToGo: string) => {
        let result;
        if(isAPackageSale())
            result = await addPackageToCart();
        else
            result = await addToCart();


        if(result.result.IsSuccess) {
            trackAddedToCart(result);


            // TODO: Enelver ce call et le remplacer par une simple fonction de logique car elle 
            // ne sert qu'à rediriger le client vers le panier.
            if (afterAddedToCartCallback != null) 
                afterAddedToCartCallback(placeToGo);
        } else {
            if (result.result.RedirectToInvalidOperation)
                toastr.error(tr("general.communicationerroroccuredpleaseretry"));
            else if (result.result.MustRedirectToCart)
            {
                // We use an alert here since we have to redirect the client to the cart after. 
                // The toast would disappear too quickly along redirection.
                alert(result.result.ErrorMessage);
                if (afterAddedToCartCallback != null) 
                    afterAddedToCartCallback(PlaceToGoEnum.GO_TO_CART);
            }
            else // Will probably never be called but in case of...
            {
                if(result.expiredTicketWarningMessage)
                    toastr.error(result.expiredTicketWarningMessage);
                else if(result.result.ErrorMessage)
                    toastr.error(result.result.ErrorMessage);
            }
        }
    }, [addPackageToCart, addToCart, afterAddedToCartCallback, isAPackageSale, trackAddedToCart]);

    const continueShopping = () => { 
        if(!disabled)
            handleAddToCart(PlaceToGoEnum.CONTINUE_SHOPPING); 
    };

    const handleOnCheckoutClick = () => { 
        if(!disabled)
            handleAddToCart(PlaceToGoEnum.GO_TO_CHECKOUT); 
    };

    return <div className='selected-ticket-footer'>
        <div className='total-container'>
            <div className='total'>{tr("Total")} {accounting.formatMoney(tickeCummul.total)}</div>
            {tickeCummul.fees > 0 && <div className='info'>{`${tr("Inclut des frais de")} ${accounting.formatMoney(tickeCummul.fees)}`}</div>}
        </div>

        {warnings?.length > 0 && <TicketSelectionWarningList warnings={warnings} alertMode={true}/>}
        {
            items?.length > 0 && <>
                <button type="button" className={cIf({"btn btn-default text-uppercase": true})} disabled={disabled} onClick={continueShopping}>
                    {tr("Ajouter et continuer mes achats")}
                </button>
                <button type="button" className={cIf({"btn btn-primary text-uppercase": true})} disabled={disabled} onClick={handleOnCheckoutClick}>
                    {tr("Passer à la caisse")}
                </button>
            </>
        }
    </div>;
}